import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Fade from "@mui/material/Fade";
import SendIcon from "@mui/icons-material/Send";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";

/**
 * Message bubble component for AI/User messages
 */
function MessageBubble({ message, isAi }) {
	return (
		<Fade in timeout={300}>
			<Box
				sx={{
					display: "flex",
					flexDirection: isAi ? "row" : "row-reverse",
					gap: 1.5,
					mb: 2,
					alignItems: "flex-start",
				}}
			>
				{/* Avatar */}
				<Box
					sx={{
						width: 32,
						height: 32,
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: isAi ? "primary.main" : "grey.300",
						color: isAi ? "white" : "grey.700",
						flexShrink: 0,
					}}
				>
					{isAi ? <SmartToyIcon sx={{ fontSize: 18 }} /> : <PersonIcon sx={{ fontSize: 18 }} />}
				</Box>

				{/* Message content */}
				<Box
					sx={{
						maxWidth: "80%",
						backgroundColor: isAi ? "grey.100" : "primary.light",
						color: isAi ? "text.primary" : "primary.contrastText",
						borderRadius: 2,
						px: 2,
						py: 1.5,
						boxShadow: 1,
					}}
				>
					<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
						{message.content}
					</Typography>
				</Box>
			</Box>
		</Fade>
	);
}

/**
 * Thinking indicator component
 */
function ThinkingIndicator({ t }) {
	return (
		<Fade in timeout={300}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					gap: 1.5,
					mb: 2,
					alignItems: "flex-start",
				}}
			>
				<Box
					sx={{
						width: 32,
						height: 32,
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "primary.main",
						color: "white",
						flexShrink: 0,
					}}
				>
					<SmartToyIcon sx={{ fontSize: 18 }} />
				</Box>
				<Box
					sx={{
						backgroundColor: "grey.100",
						borderRadius: 2,
						px: 2,
						py: 1.5,
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<CircularProgress size={16} />
					<Typography variant="body2" color="text.secondary">
						{t("feedback-ai-thinking", "Thinking...")}
					</Typography>
				</Box>
			</Box>
		</Fade>
	);
}

/**
 * Quick select options component
 */
function QuickSelectOptions({ options, onSelect, disabled }) {
	const { t } = useTranslation();

	if (!options || options.length === 0) return null;

	return (
		<Box sx={{ mb: 2 }}>
			<Stack direction="row" flexWrap="wrap" gap={1}>
				{options.map((option, index) => (
					<Chip
						key={index}
						label={option === "Other" ? t("feedback-ai-option-other", "Other (please specify)") : option}
						onClick={() => onSelect(option)}
						disabled={disabled}
						variant="outlined"
						color="primary"
						clickable
						sx={{
							"&:hover": {
								backgroundColor: "primary.light",
								color: "primary.contrastText",
							},
						}}
					/>
				))}
			</Stack>
		</Box>
	);
}

/**
 * FeedbackConversation - Chat-style UI for AI clarification questions
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of messages [{role: 'ai'|'user', content: string, options?: string[]}]
 * @param {Object} props.currentQuestion - Current AI question with options
 * @param {Function} props.onAnswer - Callback when user answers (text or selected option)
 * @param {Function} props.onSkip - Callback to skip the current question
 * @param {Function} props.onEnd - Callback to end the conversation
 * @param {boolean} props.isThinking - Whether AI is processing
 * @param {boolean} props.isComplete - Whether conversation is complete
 * @param {number} props.questionCount - Number of questions asked
 * @param {number} props.maxQuestions - Maximum number of questions
 */
export default function FeedbackConversation({
	messages = [],
	currentQuestion = null,
	onAnswer,
	onSkip,
	onEnd,
	isThinking = false,
	isComplete = false,
	questionCount = 0,
	maxQuestions = 3,
}) {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState("");
	const messagesEndRef = useRef(null);

	// Auto-scroll to bottom when messages change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, isThinking]);

	const handleOptionSelect = (option) => {
		if (option === "Other") {
			// Focus the text input for custom answer
		} else {
			// Auto-submit on option select
			onAnswer(option);
		}
	};

	const handleSubmit = () => {
		if (inputValue.trim()) {
			onAnswer(inputValue.trim());
			setInputValue("");
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};

	const remainingQuestions = maxQuestions - questionCount;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{/* Header */}
			<Box sx={{ mb: 2 }}>
				<Typography variant="subtitle1" fontWeight={600}>
					{t("feedback-conversation-title", "Help us understand better")}
				</Typography>
				{!isComplete && questionCount > 0 && (
					<Typography variant="caption" color="text.secondary">
						{t("feedback-conversation-questions-remaining", { count: remainingQuestions })}
					</Typography>
				)}
			</Box>

			{/* Messages area */}
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					maxHeight: "300px",
					mb: 2,
					pr: 1,
				}}
			>
				{/* Intro message */}
				{messages.length === 0 && !isThinking && (
					<MessageBubble
						message={{
							content: t(
								"feedback-ai-intro",
								"Thank you for your feedback! I have a few quick questions to better understand your experience."
							),
						}}
						isAi={true}
					/>
				)}

				{/* Conversation messages */}
				{messages.map((msg, index) => (
					<MessageBubble key={index} message={msg} isAi={msg.role === "ai"} />
				))}

				{/* Thinking indicator */}
				{isThinking && <ThinkingIndicator t={t} />}

				{/* Completion message */}
				{isComplete && (
					<Fade in timeout={500}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								p: 2,
								backgroundColor: "success.light",
								borderRadius: 2,
								mb: 2,
							}}
						>
							<CheckCircleIcon color="success" />
							<Typography variant="body2" color="success.dark">
								{t("feedback-ai-complete", "Thank you! Your enriched feedback has been submitted.")}
							</Typography>
						</Box>
					</Fade>
				)}

				<div ref={messagesEndRef} />
			</Box>

			{/* Quick select options */}
			{!isComplete && !isThinking && currentQuestion?.options && (
				<QuickSelectOptions
					options={currentQuestion.options}
					onSelect={handleOptionSelect}
					disabled={isThinking}
				/>
			)}

			{/* Input area */}
			{!isComplete && (
				<Box sx={{ mt: "auto" }}>
					{/* Text input */}
					<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
						<TextField
							fullWidth
							size="small"
							placeholder={t("feedback-conversation-typing-placeholder", "Type your answer here...")}
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyPress={handleKeyPress}
							disabled={isThinking}
							variant="outlined"
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: 3,
								},
							}}
						/>
						<Button
							variant="contained"
							onClick={handleSubmit}
							disabled={isThinking || !inputValue.trim()}
							sx={{
								borderRadius: 3,
								minWidth: 48,
								px: 2,
							}}
						>
							<SendIcon sx={{ fontSize: 20 }} />
						</Button>
					</Box>

					{/* Action buttons */}
					<Stack direction="row" spacing={1} justifyContent="space-between">
						<Button
							variant="text"
							size="small"
							startIcon={<SkipNextIcon />}
							onClick={onSkip}
							disabled={isThinking}
							sx={{ textTransform: "none" }}
						>
							{t("feedback-ai-skip-question", "Skip this question")}
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={onEnd}
							disabled={isThinking}
							sx={{ textTransform: "none", borderRadius: 2 }}
						>
							{t("feedback-ai-end-conversation", "Submit feedback now")}
						</Button>
					</Stack>
				</Box>
			)}
		</Box>
	);
}
